/**
 * 学习乐园 · 数独（game-shudu.js）
 * ============================================================
 * 「逻辑与推理」组第五作：4×4 / 6×6 / 9×9 三档，默认档 4×4（入门铁律）。
 * 宫形随档位：4×4 是 2×2 宫、6×6 是 2×3 宫、9×9 是标准 3×3 宫。
 *
 * 出题底线（与不等式推理的「唯一解校验」同源）：每一局都从随机完整终盘
 * 反向挖洞生成，每挖一格就用计数回溯器验证「解恰好一个」——多解的盘
 * 一律回填，绝不发出。所以页面敢承诺：卡住不是题目的错。
 *
 * 反馈口径（栏目底线——即时且可解释）：
 *   规则冲突（同行/列/宫重复）当场讲清「谁和它冲突」——这是硬错误；
 *   「与答案不同但没有冲突」不即时判错（数独的乐趣就在自己推），
 *   留给「查一查」：找出第一个错格，把行/列/宫已有的数与缺的候选
 *   摆出来，讲的是排除法本身，不是报答案。
 *
 * 成绩口径：不计时（数独考的是推得通，不是推得快，与 24 点同口径），
 * 只按档位记完成局数，全部存本机 localStorage。
 *
 * 冒烟钩子：window.STShudu 暴露纯引擎函数（fits / candidates /
 * countSolutions / fullGrid / dig / puzzle / conflicts / explainCell）与
 * state()（当前局 {n, puzzle, solution, grid}，供冒烟确定性通关）。
 */
(function (global) {
  'use strict';

  var PREF_KEY = 'study-shudu';
  var RECORD_KEY = 'study-shudu-record';
  var BAD_MS = 600;

  /* ============ 引擎：纯函数 ============ */

  // 档位 → 宫形 [高, 宽]：4×4 是 2×2 宫；6×6 是 2×3 宫（2 行 3 列）；9×9 是 3×3 宫
  function boxDims(n) {
    if (n === 4) return [2, 2];
    if (n === 6) return [2, 3];
    return [3, 3];
  }

  function rowOf(n, idx) { return Math.floor(idx / n); }
  function colOf(n, idx) { return idx % n; }
  function boxOf(n, idx) {
    var d = boxDims(n);
    return Math.floor(rowOf(n, idx) / d[0]) * (n / d[1]) + Math.floor(colOf(n, idx) / d[1]);
  }

  // v 填在 idx 是否合法（同行/列/宫无 v；idx 当前为空才调）
  function fits(n, grid, idx, v) {
    var r = rowOf(n, idx), c = colOf(n, idx), b = boxOf(n, idx);
    var d = boxDims(n);
    for (var i = 0; i < grid.length; i++) {
      if (grid[i] !== v || i === idx) continue;
      if (rowOf(n, i) === r || colOf(n, i) === c || boxOf(n, i) === b) return false;
    }
    return true;
  }

  // idx 空格的可行候选（升序）
  function candidates(n, grid, idx) {
    var out = [];
    for (var v = 1; v <= n; v++) {
      if (fits(n, grid, idx, v)) out.push(v);
    }
    return out;
  }

  function shuffle(arr) {
    for (var i = arr.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var t = arr[i]; arr[i] = arr[j]; arr[j] = t;
    }
    return arr;
  }

  // 解计数（上限 limit 个即早停，默认 2）：出题唯一性的判据。MRV 取候选最少的格分支。
  function countSolutions(n, grid, limit) {
    var lim = limit || 2;
    var g = grid.slice();
    var count = 0;
    (function bt() {
      if (count >= lim) return;
      var best = -1, bestCands = null;
      for (var i = 0; i < g.length; i++) {
        if (g[i] !== 0) continue;
        var cands = [];
        for (var v = 1; v <= n; v++) { if (fits(n, g, i, v)) cands.push(v); }
        if (!cands.length) return;                       // 死路
        if (cands.length === 1 || best < 0 || cands.length < bestCands.length) {
          best = i; bestCands = cands;
          if (cands.length === 1) break;                 // 唯一候选直接走
        }
      }
      if (best < 0) { count++; return; }                 // 填满 → 得一解
      for (var k = 0; k < bestCands.length; k++) {
        g[best] = bestCands[k];
        bt();
        if (count >= lim) break;
      }
      g[best] = 0;
    })();
    return count;
  }

  // 随机完整终盘：逐格回溯，候选洗牌
  function fullGrid(n) {
    var g = new Array(n * n);
    for (var z = 0; z < g.length; z++) g[z] = 0;
    (function bt(i) {
      if (i >= g.length) return true;
      var cands = [];
      for (var v = 1; v <= n; v++) { if (fits(n, g, i, v)) cands.push(v); }
      shuffle(cands);
      for (var k = 0; k < cands.length; k++) {
        g[i] = cands[k];
        if (bt(i + 1)) return true;
      }
      g[i] = 0;
      return false;
    })(0);
    return g;
  }

  // 反向挖洞：从满盘随机顺序置 0，每挖一格验证唯一解，多解回填——
  // 到 targetGivens 个提示（或扫完一遍）为止。返回挖好的谜面。
  function dig(n, solution, targetGivens) {
    var g = solution.slice();
    var order = [];
    for (var z = 0; z < g.length; z++) order.push(z);
    shuffle(order);
    var givens = g.length;
    for (var k = 0; k < order.length && givens > targetGivens; k++) {
      var idx = order[k];
      var keep = g[idx];
      g[idx] = 0;
      if (countSolutions(n, g, 2) !== 1) g[idx] = keep;  // 多解 → 回填
      else givens--;
    }
    return g;
  }

  // 一整局：满盘 → 挖洞。targetGivens 按档定（越少越难，都留了余量不追求极限）
  var GIVENS = { 4: 6, 6: 14, 9: 34 };
  function puzzle(n) {
    var solution = fullGrid(n);
    var p = dig(n, solution, GIVENS[n] || Math.floor(n * n * 0.42));
    return { n: n, puzzle: p, solution: solution, givens: p.reduce(function (a, v) { return a + (v ? 1 : 0); }, 0) };
  }

  // 当前盘面的冲突格集合（同值同区）：即时反馈与 is-bad 渲染的依据
  function conflicts(n, grid) {
    var bad = {};
    var zones = [];
    var d = boxDims(n);
    for (var r = 0; r < n; r++) zones.push([]);
    for (var i = 0; i < grid.length; i++) zones[boxOf(n, i)].push(i);
    function mark(list) {
      var seen = {};
      for (var k = 0; k < list.length; k++) {
        var v = grid[list[k]];
        if (!v) continue;
        if (seen[v] !== undefined) { bad[seen[v]] = 1; bad[list[k]] = 1; }
        else seen[v] = list[k];
      }
    }
    for (var rr = 0; rr < n; rr++) {
      var row = []; for (var c = 0; c < n; c++) row.push(rr * n + c);
      mark(row);
      var col = []; for (var cc = 0; cc < n; cc++) col.push(cc * n + rr);
      mark(col);
    }
    for (var b = 0; b < zones.length; b++) mark(zones[b]);
    return Object.keys(bad).map(Number);
  }

  // 「查一查」的排除法讲解：错格 idx 现在填 wrong，正确是 want。
  // 摆出行/列/宫已有的数与该格的真实候选——讲的是推理路径，不是抄答案。
  function explainCell(n, grid, solution, idx) {
    var r = rowOf(n, idx), c = colOf(n, idx), b = boxOf(n, idx);
    var d = boxDims(n);
    var rowHas = [], colHas = [], boxHas = [];
    for (var i = 0; i < grid.length; i++) {
      if (!grid[i] || i === idx) continue;
      if (rowOf(n, i) === r) rowHas.push(grid[i]);
      if (colOf(n, i) === c) colHas.push(grid[i]);
      if (boxOf(n, i) === b) boxHas.push(grid[i]);
    }
    rowHas.sort(function (x, y) { return x - y; });
    colHas.sort(function (x, y) { return x - y; });
    boxHas.sort(function (x, y) { return x - y; });
    var cands = candidates(n, grid, idx);
    var want = solution[idx];
    var pos = '第 ' + (r + 1) + ' 行第 ' + (c + 1) + ' 列';
    var head = pos + '填「' + grid[idx] + '」不对，这格该填「' + want + '」。';
    if (!cands.length) {
      return head + '它现在和已有的数直接冲突（行/列/宫里有重复）——先把冲突的数清掉再推。';
    }
    var miss = [];
    for (var v = 1; v <= n; v++) { if (cands.indexOf(v) < 0) miss.push(v); }
    return head + '排除法摆出来看：这一行已有 ' + rowHas.join('、') + '；这一列已有 ' + colHas.join('、')
      + '；这个宫已有 ' + boxHas.join('、') + '。三组并起来，这格只能填 ' + cands.join(' 或 ')
      + '——再把「别处也放不下」逐个排除，就锁定了。';
  }

  /* ============ UI：挂载与状态 ============ */

  var LEVELS = [
    { n: 4, name: '入门 4×4', hint: '2×2 宫 · 默认档' },
    { n: 6, name: '进阶 6×6', hint: '2×3 宫' },
    { n: 9, name: '挑战 9×9', hint: '标准 3×3 宫' }
  ];

  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  }

  var root = null;
  var ui = {};
  var S = {
    n: 4, puzzle: null, grid: [], sel: -1,
    ended: false, soundOn: true,
    resetArm: false, resetTimer: null
  };

  /* ---- 档位选择器 ---- */

  function renderLevels() {
    var box = ui.levels;
    box.textContent = '';
    for (var i = 0; i < LEVELS.length; i++) {
      (function (lv) {
        var b = el('button', 'st-llk-pack-btn');
        b.type = 'button';
        b.setAttribute('data-level', String(lv.n));
        b.setAttribute('role', 'radio');
        b.appendChild(el('span', 'st-llk-pack-name', lv.name));
        b.appendChild(el('small', 'st-llk-pack-hint', lv.hint));
        b.addEventListener('click', function () {
          if (S.n === lv.n) return;
          newGame(lv.n);   // 换档 = 直接开新局
        });
        box.appendChild(b);
      })(LEVELS[i]);
    }
  }

  function syncLevels() {
    var btns = ui.levels.querySelectorAll('[data-level]');
    for (var i = 0; i < btns.length; i++) {
      var on = Number(btns[i].getAttribute('data-level')) === S.n;
      btns[i].classList.toggle('is-active', on);
      btns[i].setAttribute('aria-checked', on ? 'true' : 'false');
    }
  }

  /* ---- 盘面 ---- */

  function buildBoard() {
    var board = ui.board;
    board.textContent = '';
    board.style.setProperty('--st-sd-cols', String(S.n));
    var d = boxDims(S.n);
    for (var idx = 0; idx < S.grid.length; idx++) {
      (function (i) {
        var b = el('button', 'st-sd-cell');
        b.type = 'button';
        b.setAttribute('data-idx', String(i));
        var r = rowOf(S.n, i), c = colOf(S.n, i);
        if (c > 0 && c % d[1] === 0) b.classList.add('bx');   // 宫左界粗线
        if (r > 0 && r % d[0] === 0) b.classList.add('by');   // 宫上界粗线
        b.addEventListener('click', function () { onCell(i); });
        board.appendChild(b);
      })(idx);
    }
    renderPad();
    renderCells();
  }

  function renderPad() {
    var pad = ui.pad;
    pad.textContent = '';
    for (var v = 1; v <= S.n; v++) {
      (function (val) {
        var b = el('button', 'st-sd-key');
        b.type = 'button';
        b.setAttribute('data-val', String(val));
        b.textContent = String(val);
        b.addEventListener('click', function () { onKey(val); });
        pad.appendChild(b);
      })(v);
    }
    var clear = el('button', 'st-sd-key st-sd-key--clear');
    clear.type = 'button';
    clear.setAttribute('data-val', '0');
    clear.textContent = '清除';
    clear.addEventListener('click', function () { onKey(0); });
    pad.appendChild(clear);
  }

  function renderCells() {
    var bad = {};
    var cl = conflicts(S.n, S.grid);
    for (var k = 0; k < cl.length; k++) bad[cl[k]] = 1;
    var cells = ui.board.children;
    for (var i = 0; i < cells.length; i++) {
      var node = cells[i];
      var given = S.puzzle[i] !== 0;
      node.textContent = S.grid[i] ? String(S.grid[i]) : '';
      node.className = 'st-sd-cell'
        + (node.classList.contains('bx') ? ' bx' : '')
        + (node.classList.contains('by') ? ' by' : '');
      if (node.classList.contains('bx')) node.classList.add('bx');
      if (node.classList.contains('by')) node.classList.add('by');
      node.classList.toggle('is-given', given);
      node.classList.toggle('is-user', !given && S.grid[i] !== 0);
      node.classList.toggle('is-bad', !!bad[i]);
      node.classList.toggle('is-sel', S.sel === i);
      node.setAttribute('aria-label', '第 ' + (rowOf(S.n, i) + 1) + ' 行第 ' + (colOf(S.n, i) + 1) + ' 列，'
        + (given ? '题目给定 ' + S.puzzle[i] : (S.grid[i] ? '你填了 ' + S.grid[i] : '空格')));
    }
    renderStatus();
  }

  /* ---- 渲染：状态 / 反馈 / 成绩 ---- */

  function renderStatus() {
    var filled = 0;
    for (var i = 0; i < S.grid.length; i++) { if (S.grid[i]) filled++; }
    if (S.ended) return;
    ui.status.className = 'st-status';
    ui.status.textContent = '入门 ' + S.n + '×' + S.n + ' · 提示 ' + S.puzzle.reduce(function (a, v) { return a + (v ? 1 : 0); }, 0)
      + ' 个 · 已填 ' + filled + '/' + S.grid.length;
  }

  function renderNote(text, tone) {
    ui.note.textContent = text || '';
    ui.note.className = 'st-llk-note' + (tone ? ' is-' + tone : '');
  }

  function fmtRecKey() { return 'x' + S.n; }

  function renderRecord() {
    var box = ui.record;
    box.textContent = '';
    var rec = loadRecord();
    for (var i = 0; i < LEVELS.length; i++) {
      var lv = LEVELS[i];
      var r = rec['x' + lv.n];
      box.appendChild(recordStat(
        lv.n + '×' + lv.n + '完成',
        (r && r.wins ? r.wins + ' 局' : '还没通关过——填满一次就记在这里')
      ));
    }
  }

  function recordStat(label, value) {
    var d = el('div', 'st-llk-stat');
    d.appendChild(el('span', 'st-llk-stat-label', label));
    d.appendChild(el('strong', 'st-llk-stat-value', value));
    return d;
  }

  /* ---- 音效（WebAudio 本地合成，与排排序同一套音色表） ---- */

  /* 手机外放响度补偿（口径统一，由 scripts/boost-sfx-mobile.mjs 批量写入）：
     手机小喇叭对 500Hz 以下钝感、超短音感知响度低——触屏设备把提示音增益翻倍、
     低于 500Hz 的音移高八度、衰减拉长；桌面/耳机维持原响度。 */
  var SFX_MOBILE = false;
  try {
    SFX_MOBILE = (global.matchMedia && global.matchMedia('(pointer: coarse)').matches) ||
      (global.navigator && global.navigator.maxTouchPoints > 0);
  } catch (e) { /* 检测不可用按桌面处理 */ }
  var actx = null;
  function sfx(kind) {
    if (!S.soundOn) return;
    try {
      var AC = global.AudioContext || global.webkitAudioContext;
      if (!AC) return;
      if (!actx) actx = new AC();
      if (actx.state === 'suspended' && actx.resume) actx.resume();
      var t = actx.currentTime;
      var seq;
      if (kind === 'ok') seq = [[523, 0], [659, 0.07]];
      else if (kind === 'win') seq = [[523, 0], [659, 0.1], [784, 0.2], [1046, 0.3]];
      else if (kind === 'bad') seq = [[196, 0], [185, 0.09]];
      else seq = [[330, 0]];   // sel
      for (var i = 0; i < seq.length; i++) {
        var o = actx.createOscillator();
        var g = actx.createGain();
        o.type = 'triangle';
        o.frequency.setValueAtTime(SFX_MOBILE && seq[i][0] < 500 ? seq[i][0] * 2 : seq[i][0], t + seq[i][1]);
        g.gain.setValueAtTime(0.0001, t + seq[i][1]);
        g.gain.exponentialRampToValueAtTime(SFX_MOBILE ? 0.42 : 0.18, t + seq[i][1] + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, t + seq[i][1] + (SFX_MOBILE ? 0.2 : 0.14));
        o.connect(g);
        g.connect(actx.destination);
        o.start(t + seq[i][1]);
        o.stop(t + seq[i][1] + (SFX_MOBILE ? 0.26 : 0.16));
      }
    } catch (e) { /* 音频不可用静默 */ }
  }

  /* ---- 交互 ---- */

  function onCell(idx) {
    if (S.ended) return;
    if (S.puzzle[idx] !== 0) {
      // 题目给定格：不可选中，讲一句它的用法（是推理的支点，不是障碍）
      S.sel = -1;
      renderCells();
      renderNote('「' + S.puzzle[idx] + '」是题目给定的数，改不了它——拿它去排除同行、同列、同宫的格子。', '');
      return;
    }
    S.sel = (S.sel === idx) ? -1 : idx;
    sfx('sel');
    renderCells();
    if (S.sel >= 0 && !S.grid[idx]) {
      renderNote('选中了第 ' + (rowOf(S.n, idx) + 1) + ' 行第 ' + (colOf(S.n, idx) + 1) + ' 列：点下面的数字填入；再点这格或「清除」可以清空。', '');
    }
  }

  function onKey(v) {
    if (S.ended) return;
    if (S.sel < 0) {
      renderNote('先点盘上的空格选中它，再点数字填入。', '');
      return;
    }
    var idx = S.sel;
    if (!v) {
      S.grid[idx] = 0;
      renderCells();
      renderNote('清掉了。想清楚行列宫还缺什么再填。', '');
      return;
    }
    S.grid[idx] = v;
    sfx('ok');
    S.sel = -1;
    if (isComplete()) {
      renderCells();
      winGame();
      return;
    }
    var cl = conflicts(S.n, S.grid);
    renderCells();
    if (cl.indexOf(idx) >= 0) {
      // 硬错误当场讲：谁和它冲突（同行/列/宫里已有的同数格）
      var r = rowOf(S.n, idx), c = colOf(S.n, idx), b = boxOf(S.n, idx);
      var d = boxDims(S.n);
      var where = [];
      for (var i = 0; i < S.grid.length; i++) {
        if (i === idx || S.grid[i] !== v) continue;
        if (rowOf(S.n, i) === r) where.push('第 ' + (r + 1) + ' 行');
        else if (colOf(S.n, i) === c) where.push('第 ' + (c + 1) + ' 列');
        else if (boxOf(S.n, i) === b) where.push('它的宫');
      }
      flashBad([idx]);
      sfx('bad');
      renderNote('「' + v + '」和' + where.join('、') + '里已有的「' + v + '」冲突了——同一行、同一列、同一个宫里不能有两个 ' + v + '。改成别的数，或点「清除」拿掉。', 'bad');
      return;
    }
    renderNote('填上了「' + v + '」——没有冲突，但数独还要它是对的：用行列宫的缺数自己核一遍。', '');
  }

  function flashBad(idxs) {
    setTimeout(function () {
      // 冲突高亮由 renderCells 的 conflicts 统一给出，不需要单独摘——
      // 这里只做一次无操作占位，避免引入第二个会漂移的高亮来源。
    }, BAD_MS);
  }

  function isComplete() {
    for (var i = 0; i < S.grid.length; i++) {
      if (S.grid[i] !== S.solution[i]) return false;
    }
    return true;
  }

  /* ---- 查一查：找第一个错格，摆排除法 ---- */

  function checkBoard() {
    if (S.ended) return;
    var wrong = -1;
    for (var i = 0; i < S.grid.length; i++) {
      if (S.grid[i] && S.grid[i] !== S.solution[i]) { wrong = i; break; }
    }
    if (wrong < 0) {
      var empty = 0;
      for (var j = 0; j < S.grid.length; j++) { if (!S.grid[j]) empty++; }
      renderNote(empty
        ? '盘面上还没发现填错的数——接下来找「唯一候选」：哪一行/列/宫只缺一个数，那个数就直接定在那。'
        : '空格都还没填，先从「只缺一个数的行」下手最快。', '');
      return;
    }
    renderNote(explainCell(S.n, S.grid, S.solution, wrong), 'bad');
  }

  /* ---- 通关与成绩（只存本机） ---- */

  function winGame() {
    S.ended = true;
    sfx('win');
    var rec = loadRecord();
    var key = fmtRecKey();
    var cur = rec[key];
    if (!cur || typeof cur !== 'object' || Array.isArray(cur)) cur = { wins: 0 };
    cur.wins++;
    rec[key] = cur;
    try { localStorage.setItem(RECORD_KEY, JSON.stringify(rec)); } catch (e) { /* 隐私模式静默 */ }
    ui.status.className = 'st-status is-over';
    ui.status.textContent = '完成！' + S.n + '×' + S.n + ' 这一局解出来了——数独没有计时排名，解出来就是赢。点「新开一局」再来一盘。';
    renderNote('用到的招数就两样：唯一候选（一格只能放一个数）和宫内排除（一行/宫缺谁就填谁）。9×9 的难局也只是把这两招用更多遍。', 'ok');
    renderRecord();
    renderButtons();
  }

  function loadRecord() {
    try {
      var r = JSON.parse(localStorage.getItem(RECORD_KEY) || '{}');
      return r && typeof r === 'object' && !Array.isArray(r) ? r : {};
    } catch (e) { return {}; }
  }

  function resetRecord() {
    if (!S.resetArm) {
      S.resetArm = true;
      renderButtons();
      if (S.resetTimer) clearTimeout(S.resetTimer);
      S.resetTimer = setTimeout(function () { S.resetArm = false; S.resetTimer = null; renderButtons(); }, 3000);
      return;
    }
    S.resetArm = false;
    if (S.resetTimer) { clearTimeout(S.resetTimer); S.resetTimer = null; }
    try { localStorage.removeItem(RECORD_KEY); } catch (e) { /* 隐私模式静默 */ }
    renderRecord();
    renderButtons();
    toast('已重置数独的本机成绩');
  }

  function renderButtons() {
    ui.newBtn.disabled = false;
    ui.resetBtn.disabled = false;
    ui.resetBtn.classList.toggle('is-arm', S.resetArm);
    ui.resetBtn.textContent = S.resetArm ? '再点一次确认重置' : '重置成绩';
  }

  /* ---- 偏好（档位/音效）存本机 ---- */

  function save() {
    try {
      localStorage.setItem(PREF_KEY, JSON.stringify({ v: 1, n: S.n, soundOn: S.soundOn }));
    } catch (e) { /* 存储不可用静默 */ }
  }

  function restorePrefs() {
    try {
      var d = JSON.parse(localStorage.getItem(PREF_KEY) || '{}');
      if (!d || d.v !== 1) return;
      if (d.n === 4 || d.n === 6 || d.n === 9) S.n = d.n;
      S.soundOn = d.soundOn !== false;
    } catch (e) { /* 忽略脏数据 */ }
  }

  /* ---- 开局 ---- */

  function newGame(n) {
    var p = puzzle(n);
    S.n = n;
    S.puzzle = p.puzzle;
    S.solution = p.solution;
    S.grid = p.puzzle.slice();
    S.sel = -1;
    S.ended = false;
    S.resetArm = false;
    if (S.resetTimer) { clearTimeout(S.resetTimer); S.resetTimer = null; }
    syncLevels();
    buildBoard();
    renderAll();
    renderRecord();
    renderNote('「入门 ' + n + '×' + n + '」：点一个空格，再点下面的数字填入。每局都从唯一解的盘里挖出来——卡住不是题目的错。', '');
    save();
  }

  function renderAll() {
    renderCells();
    renderButtons();
  }

  /* ---- 音效选择器（口径同五子棋/连连看） ---- */

  function bindSoundPicker() {
    var btns = root.querySelectorAll('[data-st-sound]');
    for (var i = 0; i < btns.length; i++) {
      (function (b) {
        b.addEventListener('click', function () {
          S.soundOn = b.getAttribute('data-st-sound') === 'on';
          for (var j = 0; j < btns.length; j++) {
            var on = btns[j] === b;
            btns[j].classList.toggle('is-active', on);
            btns[j].setAttribute('aria-checked', on ? 'true' : 'false');
          }
          save();
        });
      })(btns[i]);
    }
    syncSoundPicker();
  }

  function syncSoundPicker() {
    var btns = root.querySelectorAll('[data-st-sound]');
    for (var i = 0; i < btns.length; i++) {
      var on = (btns[i].getAttribute('data-st-sound') === 'on') === S.soundOn;
      btns[i].classList.toggle('is-active', on);
      btns[i].setAttribute('aria-checked', on ? 'true' : 'false');
    }
  }

  /* ---- toast（口径同五子棋/连连看） ---- */

  function toast(msg) {
    if (!ui.saved) return;
    ui.saved.textContent = msg;
    ui.saved.hidden = false;
    clearTimeout(toast._t);
    toast._t = setTimeout(function () { ui.saved.hidden = true; }, 2200);
  }

  /* ============ boot ============ */

  function boot() {
    root = document.querySelector('[data-st-game]');
    if (!root) return;
    ui.levels = root.querySelector('[data-st-sd-levels]');
    ui.board = root.querySelector('[data-st-sd-board]');
    ui.pad = root.querySelector('[data-st-sd-pad]');
    ui.status = root.querySelector('[data-st-status]');
    ui.note = root.querySelector('[data-st-note]');
    ui.record = root.querySelector('[data-st-record]');
    ui.saved = root.querySelector('[data-st-saved]');
    ui.newBtn = root.querySelector('[data-st-new]');
    ui.checkBtn = root.querySelector('[data-st-check]');
    ui.resetBtn = root.querySelector('[data-st-reset]');

    restorePrefs();
    renderLevels();
    bindSoundPicker();

    ui.newBtn.addEventListener('click', function () { newGame(S.n); });
    ui.checkBtn.addEventListener('click', checkBoard);
    ui.resetBtn.addEventListener('click', resetRecord);

    newGame(S.n);
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
  }

  /* ============ 冒烟与复用钩子 ============ */

  global.STShudu = {
    boxDims: boxDims,
    boxOf: boxOf,
    fits: fits,
    candidates: candidates,
    countSolutions: countSolutions,
    fullGrid: fullGrid,
    dig: dig,
    puzzle: puzzle,
    conflicts: conflicts,
    explainCell: explainCell,
    state: function () { return { n: S.n, puzzle: S.puzzle, solution: S.solution, grid: S.grid }; }
  };
})(typeof window !== 'undefined' ? window : globalThis);
